import { useI18n } from '@ecom/i18n';
import { useState, type ReactElement, type SyntheticEvent } from 'react';

import { Button } from './Button';
import { Field } from './Field';
import { Input } from './Input';

/** Mirrors the API's rule for a password chosen by its owner (`CHOSEN_PASSWORD_MIN_LENGTH`). */
export const CHOSEN_PASSWORD_MIN_LENGTH = 12;

export interface ChangePasswordFormProps {
  /** Rejects with the failure; the form then shows `error` (the caller translates it) and keeps nothing but the typed text. */
  readonly onSubmit: (values: { currentPassword: string; newPassword: string }) => Promise<void>;
  /** The caller's translated message for a refused request (wrong current password, throttled...). */
  readonly error?: string | null;
  readonly busy?: boolean;
  /** « Mot de passe provisoire » when the current password is a provisional one. */
  readonly currentLabel?: string;
}

/**
 * The form that replaces a password: the current one, the new one twice.
 *
 * Validated here for the person's comfort (length, match, different from the current one) and validated again by the API, which is the
 * authority. The password fields are never prefilled, never autofilled with a saved value for the CURRENT field (`current-password`
 * lets a password manager help) and never reported to anything but `onSubmit`.
 */
export function ChangePasswordForm({
  onSubmit,
  error = null,
  busy = false,
  currentLabel,
}: ChangePasswordFormProps): ReactElement {
  const { t } = useI18n();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const currentError = current === '' ? t('common.changePassword.currentRequired') : undefined;
  const nextError =
    next.length < CHOSEN_PASSWORD_MIN_LENGTH
      ? t('common.changePassword.tooShort')
      : next === current
        ? t('common.changePassword.same')
        : undefined;
  const confirmError = confirm !== next ? t('common.changePassword.mismatch') : undefined;
  const valid = currentError === undefined && nextError === undefined && confirmError === undefined;

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    setSubmitted(true);
    if (!valid || busy) return;
    void onSubmit({ currentPassword: current, newPassword: next }).catch(() => undefined);
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="ecom-change-password">
      <Field
        label={currentLabel ?? t('common.changePassword.current')}
        {...(submitted && currentError !== undefined ? { error: currentError } : {})}
      >
        {(bind) => (
          <Input
            {...bind}
            type="password"
            autoComplete="current-password"
            autoFocus
            value={current}
            onChange={(event) => {
              setCurrent(event.target.value);
            }}
          />
        )}
      </Field>

      <Field
        label={t('common.changePassword.new')}
        hint={t('common.changePassword.rules')}
        {...(submitted && nextError !== undefined ? { error: nextError } : {})}
      >
        {(bind) => (
          <Input
            {...bind}
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(event) => {
              setNext(event.target.value);
            }}
          />
        )}
      </Field>

      <Field
        label={t('common.changePassword.confirm')}
        {...(submitted && confirmError !== undefined ? { error: confirmError } : {})}
      >
        {(bind) => (
          <Input
            {...bind}
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => {
              setConfirm(event.target.value);
            }}
          />
        )}
      </Field>

      {error !== null ? (
        <p role="alert" className="ecom-change-password__error">
          {error}
        </p>
      ) : null}

      <Button type="submit" loading={busy} className="ecom-change-password__submit">
        {t('common.changePassword.submit')}
      </Button>
    </form>
  );
}
